import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useUserAccess } from '@/hooks/useUserAccess';
import { ClipboardCheck, BookOpen, FlaskConical, Sparkles, PackageCheck, Route, type LucideIcon } from 'lucide-react';


export interface OnboardingChecklistItem {
  key: string;
  label: string;
  description: string;
  href: string;
  icon: LucideIcon;
  done: boolean;
}

async function existsRow(
  table: 'participant_diagnostics' | 'content_progress' | 'challenges' | 'forum_threads' | 'forum_responses',
  column: 'user_id' | 'author_id',
  userId: string
) {
  const { data } = await supabase
    .from(table as any)
    .select('id')
    .eq(column, userId)
    .limit(1)
    .maybeSingle();
  return !!data;
}

async function hasProjectEngagement(userId: string) {
  // Señal A: usó el asistente de prototipado (chat_sessions con contexto de proyecto/prototipado)
  const { data: chat } = await supabase
    .from('chat_sessions')
    .select('id')
    .eq('user_id', userId)
    .in('context_type', ['prototype', 'project'])
    .limit(1)
    .maybeSingle();
  if (chat) return true;
  return false;
}

async function hasSubmittedDeliverable(userId: string) {
  // Entregables de taller (cohortes con acompañamiento)
  const { data } = await supabase
    .from('participant_deliverables')
    .select('id')
    .eq('user_id', userId)
    .in('status', ['submitted', 'validated'])
    .limit(1)
    .maybeSingle();
  if (data) return true;

  // Entrega desde el módulo de proyectos (usuarios sin cohorte guiada)
  const { data: project } = await supabase
    .from('participant_projects')
    .select('id')
    .eq('user_id', userId)
    .eq('submitted', true)
    .limit(1)
    .maybeSingle();
  return !!project;
}

async function hasSelfPacedEnrollment(userId: string) {
  const { data } = await supabase
    .from('cohort_participants')
    .select('id, cohorts!inner(is_perpetual)')
    .eq('user_id', userId)
    .eq('cohorts.is_perpetual', true)
    .limit(1)
    .maybeSingle();
  return !!data;
}

export function useOnboardingChecklist() {
  const { user } = useAuth();
  const { hasPaidAccess, isLoading: accessLoading } = useUserAccess();

  const query = useQuery({
    queryKey: ['onboarding-checklist', user?.id, hasPaidAccess],
    enabled: !!user?.id && !accessLoading,
    queryFn: async () => {
      const uid = user!.id;
      const [diag, content, challenge, project, deliverable, selfPaced] = await Promise.all([
        existsRow('participant_diagnostics', 'user_id', uid),
        existsRow('content_progress', 'user_id', uid),
        existsRow('challenges', 'user_id', uid),
        hasProjectEngagement(uid),
        hasSubmittedDeliverable(uid),
        hasPaidAccess ? hasSelfPacedEnrollment(uid) : Promise.resolve(false),
      ]);


      const items: OnboardingChecklistItem[] = [
        {
          key: 'diagnostic',
          label: 'Realiza tu diagnóstico',
          description: 'Responde el diagnóstico inicial para conocer tu nivel actual de madurez en IA.',
          href: '/diagnostic',
          icon: ClipboardCheck,
          done: diag,
        },
        {
          key: 'content',
          label: 'Explora los contenidos',
          description: 'Navega por las categorías del catálogo y mira al menos un video o lectura.',
          href: '/content',
          icon: BookOpen,
          done: content,
        },
        ...(hasPaidAccess
          ? [
              {
                key: 'self-paced-route',
                label: 'Inscríbete a una ruta de autoaprendizaje',
                description: 'Elige tu primera ruta de autoaprendizaje y avanza a tu ritmo.',
                href: '/rutas',
                icon: Route,
                done: selfPaced,
              } as OnboardingChecklistItem,
            ]
          : []),
        {

          key: 'challenge-lab',
          label: 'Identifica un proyecto',
          description: 'Entra al Laboratorio de Retos para descubrir un proyecto que impacte tu rol o tu empresa.',
          href: '/challenge-lab',
          icon: FlaskConical,
          done: challenge,
        },
        {
          key: 'project',
          label: 'Desarrolla tu Proyecto',
          description: 'Entra a un proyecto y envía al menos un mensaje al asistente de prototipado.',
          href: '/challenge',
          icon: Sparkles,
          done: project,
        },
        {
          key: 'deliverable',
          label: 'Termina tu primer proyecto',
          description: 'Envía el entregable de un proyecto con las respuestas de las preguntas completas.',
          href: '/challenge?onboarding=deliverable',
          icon: PackageCheck,
          done: deliverable,
        },
      ];

      return {
        items,
        completed: items.filter((i) => i.done).length,
        total: items.length,
        allDone: items.every((i) => i.done),
      };
    },
    staleTime: 60 * 1000,
  });

  return {
    items: query.data?.items ?? [],
    completed: query.data?.completed ?? 0,
    total: query.data?.total ?? (hasPaidAccess ? 6 : 5),
    allDone: query.data?.allDone ?? false,
    isLoading: query.isLoading,
  };
}
